// src/pages/Main.jsx

import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";
import PageText from "../components/PageText";
import PageTitle from "../components/PageTitle";
import SectionBox from "../components/SectionBox";
import DiscordPresenceMarquee from "../components/DiscordPresenceMarquee";
import { fetchFleetStatsCached } from "../lib/fleetStatsCache";
import { motion } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const faqItems = [
  {
    q: "Is CHAOTIK recruiting?",
    a: "Yes. We're actively recruiting 18+ Star Citizen players across EU, US and Australia. Hop on the Discord to start the process.",
  },
  {
    q: "What gameplay does CHAOTIK do?",
    a: "Everything. CHAOTIK is multi-role — combat drops, industrial operations, bounty hunting, cargo runs, exploration, full-scale tactical operations. Anything permitted by the CIG/RSI Terms of Service is allowed.",
  },
  {
    q: "What timezones is CHAOTIK active in?",
    a: "We have members across EU, US and AUS, so wings form 24/7. There is almost always someone online to fly with.",
  },
  {
    q: "Is there an age requirement?",
    a: "Yes. CHAOTIK is 18+ only. The community and voice comms are adult.",
  },
  {
    q: "How do I join?",
    a: "Join our Discord, fly with us a couple of times, and once it clicks we'll point you at the application on our RSI org page so the tag goes live in-game.",
  },
];

const ranksForJsonLd = [
  { rank: "Member", description: "New recruit joining the org." },
  { rank: "Operative", description: "Senior member acting as informal leader and teacher." },
  { rank: "Officer", description: "Backbone of the Leadership Corps." },
  { rank: "XO", description: "Second-in-command and member of the High Council." },
  { rank: "Commander", description: "Senior-most founding members on the High Council." },
];

const DISCORD_WIDGET_URL = "https://discord.com/api/guilds/1350390959938211850/widget.json";
const DISCORD_REFRESH_INTERVAL = 60_000;

export default function Main() {
  const location = useLocation();
  const [presenceCount, setPresenceCount] = useState(0);
  const [guildMembers, setGuildMembers] = useState([]);
  const [guildChannels, setGuildChannels] = useState([]);
  const [presenceLoadError, setPresenceLoadError] = useState(false);
  const [fleetStats, setFleetStats] = useState(null);
  const fleetCards = useMemo(() => {
    const summary = fleetStats?.summary || {};
    const toDisplay = (value) => (Number.isFinite(value) ? value.toLocaleString() : "—");

    return [
      { label: "Total # of Ships", value: toDisplay(Number(summary?.totalShips)) },
      { label: "Capital", value: toDisplay(Number(summary?.capital)) },
      { label: "Large", value: toDisplay(Number(summary?.large)) },
      { label: "Medium", value: toDisplay(Number(summary?.medium)) },
      { label: "Small", value: toDisplay(Number(summary?.small)) },
      { label: "Vehicles", value: toDisplay(Number(summary?.vehicles)) },
      { label: "Snub", value: toDisplay(Number(summary?.snub)) },
    ];
  }, [fleetStats]);

  useEffect(() => {
    let mounted = true;
    fetchFleetStatsCached()
      .then((statsData) => {
        if (!mounted) return;
        if (!statsData?.stats?.summary) {
          console.warn("Fleet stats response loaded but summary is missing; fleet cache may be unseeded.");
        }
        setFleetStats(statsData?.stats || null);
      })
      .catch((err) => console.error("Failed to load fleet stats", err));

    return () => {
      mounted = false;
    };
  }, []);
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100); // Wait for DOM to render
      }
    }
  }, [location]);

  useEffect(() => {
    let isActive = true;

    async function loadPresence() {
      try {
        const response = await fetch(DISCORD_WIDGET_URL, { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!isActive) return;

        setPresenceCount(Number(data?.presence_count || 0));
        setGuildMembers(Array.isArray(data?.members) ? data.members : []);
        setGuildChannels(Array.isArray(data?.channels) ? data.channels : []);
        setPresenceLoadError(false);
      } catch (error) {
        if (!isActive) return;
        console.error("Failed to load Discord widget data", error);
        setPresenceCount(0);
        setGuildMembers([]);
        setGuildChannels([]);
        setPresenceLoadError(true);
      }
    }

    loadPresence();
    const interval = setInterval(loadPresence, DISCORD_REFRESH_INTERVAL);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, []);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "CHAOTIK Org Ranks",
    itemListElement: ranksForJsonLd.map(({ rank, description }, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: rank,
      description,
    })),
  };

  return (
    <Layout>
      <Helmet>
        <title>CHAOTIK | Multi-Role Star Citizen Organization</title>
        <meta
          name="description"
          content="Multi-role 24/7 Star Citizen org. Combat, industry, bounty hunting, cargo, exploration, tactical ops. 18+, EU/US/AUS, all CIG-permitted gameplay welcome."
        />
        <link rel="canonical" href="https://chaotik.org/" />
        <meta property="og:title" content="CHAOTIK | Multi-Role Star Citizen Organization" />
        <meta
          property="og:description"
          content="Multi-role 24/7 Star Citizen org for 18+ players across EU/US/AUS — combat, industry, bounty hunting, cargo, exploration and tactical operations. Featured in CIG's Tactical Strike Groups Org Spotlight."
        />
        <meta property="og:url" content="https://chaotik.org/" />
        <meta name="twitter:title" content="CHAOTIK | Multi-Role Star Citizen Organization" />
        <meta
          name="twitter:description"
          content="Multi-role 24/7 Star Citizen org. Combat, industry, bounty hunting, cargo, exploration, tactical ops. 18+, EU/US/AUS — all CIG-permitted gameplay welcome."
        />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>
      <div className="mt-6 w-full px-4">
        <DiscordPresenceMarquee
          className="w-full"
          autoFetch={false}
          membersOverride={guildMembers}
          channelsOverride={guildChannels}
          presenceCountOverride={presenceCount}
          hadErrorOverride={presenceLoadError}
        />
      </div>
      <div className="origin-top scale-90">
        <div className="flex flex-col justify-center items-center text-center mt-10 sm:mt-14">
        {/* Logo */}
        <motion.img
          src="/assets/logo-main.png"
          alt="CHAOTIK Star Citizen organization logo"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="w-40 sm:w-60 md:w-80 lg:w-[20rem] xl:w-[32rem] mb-10"
        />

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <PageTitle>
            <span className="font-orbitron text-white text-5xl sm:text-6xl md:text-7xl">
              Welcome to CHAOTIK!
            </span>
          </PageTitle>
          <p className="mt-4 text-white/70 text-sm sm:text-base font-light tracking-[0.35em] uppercase">
            A STAR CITIZEN® ORGANIZATION
          </p>
        </motion.div>

        {/* Scroll arrow */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="mt-4"
        >
          <ScrollLink
            to="intro-text"
            smooth={true}
            duration={800}
            offset={-80}
            className="cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-10 h-10 text-white animate-bounce"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </ScrollLink>
        </motion.div>

        {/* MAIN Section */}
        <div id="intro" className="mt-12 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageText>
              <p className="text-center break-normal">
                Chaotik was founded in 2955 by a group of citizens whose
                free-spirited nature refused to be bound by strict rules of
                large orgs—citizens who shared a vision of creating a sanctuary
                in the Verse for people like them. Experienced players that
                instead of showing off shiny org armors, prefer to strike quickly
                and efficiently.
              </p>
            </PageText>
            <PageText>
              <p className="text-center break-normal p-2 sm:p-4 md:p-6 lg:p-8 xl:p-10">
                CHAOTIK welcomes all citizens—whether you love industry or PvP, you’ll
                always find someone to play with. We’re gamers at heart, drawn to fun
                yet challenging loops. As a group, we value camaraderie and welcome
                anyone who shares a passion for the Verse.
              </p>
            </PageText>

            <hr className="my-16 border-t border-white/30 w-3/4 sm:w-2/3 md:w-1/2 mx-auto" />

            <PageText>
              Chaotik allows all gameplay permitted by the CIG/RSI Terms of Service. If
              it’s allowed in Star Citizen, it’s allowed by us. We strive to be on the
              bleeding edge of new codelines and content when it’s released.
            </PageText>
            <PageText>
              <p className="text-center break-normal p-2 sm:p-4 md:p-4 lg:p-6 xl:p-8">
                Flexibility is key. We prioritize members over rigid structures. In any
                conflict between “org needs” and “member wants,” we’ll always side with
                our community.
              </p>
            </PageText>
          </SectionBox>
        </div>

        {/* ORG QUICK FACTS Section (featured-snippet target) */}
        <div id="quickfacts" className="mt-16 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageTitle as="h2" className="font-orbitron text-center mb-6">Org Quick Facts</PageTitle>
            <PageText>
              <ul className="text-left max-w-2xl mx-auto list-disc list-inside space-y-2 sm:space-y-3 px-2 sm:px-6">
                <li><strong>210+ members</strong> and growing.</li>
                <li><strong>Founded:</strong> in-lore 2955.</li>
                <li><strong>Gameplay:</strong> multi-role &mdash; combat, industry, bounty hunting, cargo, exploration, tactical operations.</li>
                <li><strong>Language:</strong> English-speaking community.</li>
                <li><strong>Age:</strong> 18+ only.</li>
                <li><strong>Coverage:</strong> active members across EU, US and AUS &mdash; wings form 24/7.</li>
                <li><strong>Gameplay policy:</strong> all gameplay permitted by CIG/RSI Terms of Service is allowed.</li>
                <li>
                  <strong>Recognition:</strong> featured in{" "}
                  <a
                    href="https://robertsspaceindustries.com/en/comm-link/transmission/21082-Tactical-Strike-Groups-Org-Spotlight"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#f87171] underline decoration-white/30 underline-offset-4 hover:text-white"
                  >
                    CIG's Tactical Strike Groups Org Spotlight (May 2026)
                  </a>.
                </li>
              </ul>
            </PageText>
          </SectionBox>
        </div>

        <div className="mt-24 w-full flex justify-center">
          <SectionBox>
            <PageTitle as="h2" className="font-orbitron text-center mb-10">A very Chaotik year</PageTitle>
            <div className="w-full max-w-5xl mx-auto">
              <div className="relative w-full overflow-hidden rounded-lg border border-white/10 bg-black/40 shadow-lg aspect-video">
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src="https://www.youtube.com/embed/AnuPdQi85-Q"
                  title="A very Chaotik year"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </SectionBox>
        </div>

        {/* STRUCTURE Section */}
        <div id="structure" className="mt-24 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageTitle as="h2" className="font-orbitron text-center mb-10">Structure</PageTitle>
            <PageText>
              <p className="text-center break-normal">
                The following outlines the hierarchical structure of CHAOTIK. Beyond the
                standard rank progression, the organization features two governing bodies:
                the Leadership Corps, consisting of all Officers and above, and the High
                Council, reserved exclusively for Commanders and XOs.
              </p>
            </PageText>

            <div className="space-y-10">
              {[
                {
                  rank: "Member",
                  description: (
                    <>
                      Regular members of the organization who make up the heart of CHAOTIK.
                      Initially, a new member joining our{" "}
                      <a
                        className="text-[#f87171] underline decoration-white/30 underline-offset-4 hover:text-white"
                        href="https://discord.gg/CHAOTIK"
                        rel="noreferrer"
                        target="_blank"
                      >
                        Discord
                      </a>{" "}
                      server will do so as an &quot;Aspirant&quot; before completing the
                      onboarding process. Once onboarding has been finalized, the new
                      member will receive the Member role.
                    </>
                  ),
                },
                {
                  rank: "Operative",
                  description: (
                    <>
                      Operatives are senior members who are selected by the Leadership
                      Corps based on their character and proficiency of the gameplay
                      mechanics and loops in the &quot;Verse. Operatives act as informal
                      leaders and teachers, and are chosen for various assignments related
                      to internal operations.
                    </>
                  ),
                },
                {
                  rank: "Officer",
                  description: (
                    <>
                      Officers are selected by the High Council from the Operatives based
                      on character, knowledge, and proficiency. Officers are the backbone
                      of the Leadership Corps and are the first line of formal leaders for
                      the org.
                    </>
                  ),
                },
                {
                  rank: "XO",
                  description: (
                    <>
                      XOs serve as second-in-command over the org and are members of the
                      High Council. XOs are made up of some of the original founding
                      members.
                    </>
                  ),
                },
                {
                  rank: "Commander",
                  description: (
                    <>
                      Commanders are the senior-most founding members and are also members
                      of the High Council. Commanders act as supreme leaders of the
                      organization.
                    </>
                  ),
                },
              ].map(({ rank, description }) => (
                <div
                  key={rank}
                  className="flex flex-col sm:flex-row items-center sm:items-center sm:justify-start gap-4 sm:gap-10 w-full text-left"
                >
                  <div className="flex justify-center sm:justify-start w-[150px] sm:w-[180px] md:w-[220px] lg:w-[256px] xl:w-[340px] 2xl:w-[340px] shrink-0">
                    <img
                      src={`/assets/${rank.toLowerCase()}.png`}
                      alt={`CHAOTIK Star Citizen ${rank} rank badge`}
                      className="w-full object-contain"
                    />
                  </div>
                  <div className="max-w-xl">
                    <h3 className="font-orbitron text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-center sm:text-left">
                      {rank}
                    </h3>
                    <p className="mt-2 text-sm sm:text-base md:text-lg text-white/80">
                      {description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </SectionBox>
        </div>

        {/* FLEET Section */}
        <div id="fleet" className="mt-24 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageTitle as="h2" className="font-orbitron text-center mb-10">Fleet</PageTitle>

            <PageText>
              <p className="text-center break-normal p-4">
                Our fleet is ready for any mission. From massive capital ships to agile
                snubs, CHAOTIK maintains a versatile lineup to support every operation.
              </p>
            </PageText>

            <div className="mt-8">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 shadow-lg">
                <div className="flex flex-col items-center justify-center">
                  <span className="font-orbitron text-3xl sm:text-4xl text-[#f87171] font-bold text-center">{fleetCards[0]?.value || "—"}</span>
                  <span className="mt-2 text-sm sm:text-base text-white/80 uppercase tracking-wide text-center">
                    {fleetCards[0]?.label || "Total # of Ships"}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {fleetCards.slice(1).map(({ label, value }) => (
                <div
                  key={label}
                  className="flex flex-col items-center justify-center bg-white/5 border border-white/10 rounded-lg p-6 shadow-lg"
                >
                  <span className="font-orbitron text-2xl sm:text-3xl text-[#f87171] font-bold text-center">{value}</span>
                  <span className="mt-2 text-sm sm:text-base text-white/80 uppercase tracking-wide text-center">
                    {label}
                  </span>
                </div>
              ))}
            </div>

            <a href="/fleet" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-red-500 px-6 py-3 text-lg font-semibold text-white hover:bg-red-400 transition">
              Explore Our Fleet →
            </a>
          </SectionBox>
        </div>

        {/* MEDIA Section */}
        <div id="media" className="mt-24 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageTitle className="font-orbitron text-center mb-10">Media</PageTitle>

            {/* Screenshots card — centered above the social grid */}
            <div className="flex justify-center w-full">
              <a
                href="/media/Media"
                className="relative w-full sm:w-1/2 h-64 sm:h-80 md:h-96 rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300"
                style={{
                  backgroundImage: "url(/assets/media-screenshots.jpg)",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <span className="font-bebas text-white text-3xl sm:text-4xl md:text-5xl tracking-wider uppercase">
                    Screenshots
                  </span>
                </div>
              </a>
            </div>

            {/* Social media cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full mt-6">
              {[
                {
                  name: "Twitch",
                  href: "/media/Twitch",
                  bg: "/assets/media-video.jpg",
                },
                {
                  name: "YouTube",
                  href: "/media/youtube",
                  bg: "/assets/media-youtube.jpg",
                },
                {
                  name: "Kick",
                  href: "https://kick.com/chaotiksc",
                  bg: "/assets/media-kick.png",
                },
                {
                  name: "TikTok",
                  href: "https://www.tiktok.com/@chaotik.sc",
                  bg: "/assets/media-tiktok.jpg",
                },
                {
                  name: "Facebook",
                  href: "https://www.facebook.com/ChaotikSC",
                  bg: "/assets/media-facebook.jpg",
                },
                {
                  name: "X",
                  href: "https://x.com/Chaotik_SC",
                  bg: "/assets/media-twitter.jpg",
                },
              ].map((section) => (
                <a
                  key={section.name}
                  href={section.href}
                  className="relative h-64 sm:h-80 md:h-96 rounded-xl overflow-hidden shadow-lg hover:scale-105 transition-transform duration-300"
                  style={{
                    backgroundImage: `url(${section.bg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="font-bebas text-white text-3xl sm:text-4xl md:text-5xl tracking-wider uppercase">
                      {section.name}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </SectionBox>
        </div>

        {/* JOIN US Section */}
        <div id="apply" className="mt-24 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageTitle as="h2" className="font-orbitron">Join Us</PageTitle>
            <PageText>
              <p className="text-center break-normal p-4">
                Interested in joining CHAOTIK? Click below to apply and become part
                of our growing community.
              </p>
            </PageText>

            <div className="mt-6 flex justify-center">
              <Link
                to="/apply"
                className="font-orbitron bg-[#96000b] text-black text-lg sm:text-xl md:text-2xl font-bold px-6 py-3 rounded-md transition-all duration-300 hover:bg-red-800 hover:scale-105 active:bg-white active:text-black"
              >
                Join Us
              </Link>
            </div>
          </SectionBox>
        </div>

        {/* RECRUITMENT FAQ Section */}
        <div id="faq" className="mt-24 w-full flex justify-center scroll-mt-32">
          <SectionBox>
            <PageTitle as="h2" className="font-orbitron text-center mb-6">Recruitment FAQ</PageTitle>
            <div className="space-y-6 text-left max-w-3xl mx-auto px-2 sm:px-4">
              {faqItems.map(({ q, a }) => (
                <div key={q}>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-white">{q}</h3>
                  <p className="mt-2 text-sm sm:text-base md:text-lg text-white/80">{a}</p>
                </div>
              ))}
            </div>
          </SectionBox>
        </div>


        {/* DISCORD Section */}
        <div
          id="discord"
          className="mt-24 w-full flex justify-center scroll-mt-32 mb-24"
        >
          <SectionBox>
            <PageText>
              <p className="text-center break-normal p-4">
                If you have any questions or just want to hang out with us – join our
                Discord server!
              </p>
            </PageText>

            <div className="mt-6 flex justify-center">
              <a
                href="https://discord.gg/CHAOTIK"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/assets/discord.png"
                  alt="Join the CHAOTIK Star Citizen Discord server"
                  className="w-48 sm:w-64 md:w-72 lg:w-80 xl:w-96 hover:scale-105 transition-transform duration-300"
                />
              </a>
            </div>
          </SectionBox>
        </div>

        <div className="w-full flex flex-col items-center justify-center mb-16 px-4">
          <img
            src="/assets/Community_logo_black.png"
            alt="CHAOTIK community logo"
            className="w-32 sm:w-44 md:w-52 lg:w-56 xl:w-64"
          />
          <p className="mt-4 text-center text-[10pt] text-white/60">
            Star Citizen®, Roberts Space Industries® and Cloud Imperium ® are registered trademarks of Cloud Imperium Rights LLC.
          </p>
          <p className="mt-2 text-center text-[10pt] text-white/60">
            © {new Date().getFullYear()} Chaotik. All rights reserved.
            <br />
            All content and/or creative works associated with Chaotik, whether published on this website or on any other platform(s), including but not limited to social media pages or online forums, is the property of Chaotik or its respective creators and is protected by applicable copyright laws.
            <br />
            No content may be copied, reproduced, distributed, or used without prior written permission from Chaotik.
            <br />
            Chaotik is an independent organization and is not affiliated with, endorsed by, or sponsored by Cloud Imperium Games or Roberts Space Industries.
            <br />
            All game-related content and assets remain the property of their respective owners.
          </p>
        </div>
        </div>
      </div>
    </Layout>
  );
}
